// ClearFund Trading Terminal — v3.0 (multi-file)
// Refactored from monolith for maintainability

import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { INSTRUMENTS, CATEGORIES, TIMEFRAMES, DEFAULT_SETTINGS, BASE_PRICES, fmt } from "./constants";
import { ColorPicker, SettingsModal } from "./SettingsModal";
import { ContextMenu } from "./ContextMenu";
import {
  WatchlistItem, Clock, PriceDisplay, PerformanceCalendar,
  BracketOrderPanel, DOMPanel, CandleTimer, OHLCBar,
  RSIPanel, AddIndicatorForm
} from "./components_misc";
import { useMarketData } from "./lib/useMarketData";
import { trades as tradesApi, accounts } from "./lib/api";
import { CandleChart } from "./CandleChart";
import { TradeReplay } from "./TradeReplay";
import { EconomicCalendarStrip, ECONOMIC_EVENTS, EVENT_TYPES } from "./EconomicCalendar";
import { AnalyticsDashboard } from "./AnalyticsDashboard";

export default function TradingTerminal({ account: propAccount, onNavigate, onAccountRefresh }) {
  const [symbol,setSymbol]=useState("NQ");
  const [timeframe,setTimeframe]=useState("5m");
  const [qty,setQty]=useState(1);
  const [orderType,setOrderType]=useState("market");
  const [positions,setPositions]=useState([]);
  const [history,setHistory]=useState([]);
  const [tab,setTab]=useState("positions");
  const [toasts,setToasts]=useState([]);
  const [account,setAccount]=useState(propAccount||null);
  const [closingId,setClosingId]=useState(null);
  const chartPriceRef=useRef(null);
  const drawingsRef=useRef(null);
  const [chartSettings,setChartSettings]=useState({...DEFAULT_SETTINGS});
  const [showSettings,setShowSettings]=useState(false);
  const [showReplay,setShowReplay]=useState(false);
  const [showAnalytics,setShowAnalytics]=useState(false);
  const [enabledEventTypes,setEnabledEventTypes]=useState(Object.keys(EVENT_TYPES));
  const [showCalStrip,setShowCalStrip]=useState(true);
  const [ctxMenu,setCtxMenu]=useState(null);
  const [activeTool,setActiveTool]=useState(null);
  const [sidePanel,setSidePanel]=useState(null); // 'calendar'|'watchlist'|null
  const [showHotkeys,setShowHotkeys]=useState(false);
  const [limitPrice,setLimitPrice]=useState('');
  const [stopPrice,setStopPrice]=useState('');
  const [alerts,setAlerts]=useState([]); // [{id,price,dir,triggered}]
  const alertIdRef=useRef(0);
  const [indicators,setIndicators]=useState([
    {id:1,type:"EMA", period:9,  color:"#00c896",visible:true, dash:false},
    {id:2,type:"EMA", period:21, color:"#f5a623",visible:true, dash:false},
    {id:3,type:"SMA", period:50, color:"#4db8ff",visible:false,dash:true},
    {id:4,type:"SMA", period:200,color:"#a78bfa",visible:false,dash:true},
    {id:5,type:"VWAP",period:1,  color:"#ff9800",visible:false,dash:false},
    {id:6,type:"BB",  period:20, color:"#4db8ff",visible:false,dash:false,mult:2},
    {id:7,type:"RSI", period:14, color:"#a78bfa",visible:false,dash:false},
  ]);
  const [showIndicators,setShowIndicators]=useState(false);
  const [tradeNotes,setTradeNotes]=useState({}); // {tradeId: note text}
  const [dailyGoal,  setDailyGoal ]=useState(500);
  const [showRiskBar,setShowRiskBar]=useState(true);
  const [chartFullscreen,setChartFullscreen]=useState(false);
  const [rightPanelCollapsed,setRightPanelCollapsed]=useState(false);
  const [trailStop,  setTrailStop ]=useState(null);  // {posId,ticks,highWater}
  const [beStopIds,  setBeStopIds ]=useState(new Set());
  const [showSymSearch,setShowSymSearch]=useState(false);
  const [symQuery,setSymQuery]=useState('');
  const [rightW,setRightW]=useState(260);
  const [bottomH,setBottomH]=useState(185);
  const draggingRef=useRef(null); // 'right' | 'bottom' | null

  // Global mousemove/mouseup for reliable drag (works even if cursor leaves handle)
  useEffect(()=>{
    const onMove=(e)=>{
      if(!draggingRef.current) return;
      e.preventDefault();
      if(draggingRef.current.type==='right'){
        const dx=draggingRef.current.startX-e.clientX;
        setRightW(w=>Math.max(200,Math.min(480,draggingRef.current.startW+dx)));
      }
      if(draggingRef.current.type==='bottom'){
        const dy=draggingRef.current.startY-e.clientY;
        setBottomH(h=>Math.max(80,Math.min(520,draggingRef.current.startH+dy)));
      }
    };
    const onUp=()=>{
      draggingRef.current=null;
      document.body.style.cursor='';
      document.body.style.userSelect='';
      document.body.classList.remove('drag-ew','drag-ns');
    };
    window.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    return()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  const tvSym=symbol==='NQ'?'NQM5':symbol==='ES'?'ESM5':symbol==='MNQ'?'MNQM5':symbol==='MES'?'MESM5':symbol;
  const {lastPrice:livePrice,bid:liveBid,ask:liveAsk,connected:mdConnected}=useMarketData(tvSym);
  const inst=INSTRUMENTS[symbol]||INSTRUMENTS.NQ;
  const chartEvents = ECONOMIC_EVENTS.filter(e => enabledEventTypes.includes(e.type));
  const curP=chartPriceRef.current||BASE_PRICES[symbol]||21850;
  const bid=liveBid??(curP-inst.tickSize);
  const ask=liveAsk??(curP+inst.tickSize);

  const playTone = (freq=440, dur=0.12, type='sine', vol=0.15) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = type; osc.frequency.value = freq;
      gain.gain.setValueAtTime(vol, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      osc.start(); osc.stop(ctx.currentTime + dur);
    } catch(e) {}
  };
  const playSounds = {
    buy:   () => { playTone(523, 0.08, 'sine', 0.12); setTimeout(()=>playTone(659, 0.08, 'sine', 0.10), 80); },
    sell:  () => { playTone(440, 0.08, 'sine', 0.12); setTimeout(()=>playTone(349, 0.08, 'sine', 0.10), 80); },
    close: () => playTone(392, 0.15, 'triangle', 0.12),
    alert: () => { playTone(880, 0.1, 'sine', 0.15); setTimeout(()=>playTone(880, 0.1, 'sine', 0.12), 180); },
    error: () => playTone(220, 0.25, 'sawtooth', 0.1),
  };
  const [soundEnabled, setSoundEnabled] = useState(true);
  const sound = (k) => { if (soundEnabled) playSounds[k]?.(); };

  const toast=(msg,type="info")=>{const id=Date.now();setToasts(t=>[...t,{id,msg,type}]);setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),4000);};

  // Check price alerts + trailing/BE stops every render tick
  useEffect(()=>{
    const cur=chartPriceRef.current||BASE_PRICES[symbol];
    if(!cur) return;
    // Price alerts
    if(alerts.length){
      let anyTriggered=false;
      setAlerts(al=>al.map(a=>{
        if(a.triggered||a.symbol!==symbol) return a;
        const hit=(a.dir==="above"&&cur>=a.price)||(a.dir==="below"&&cur<=a.price);
        if(hit){anyTriggered=true; return {...a,triggered:true};}
        return a;
      }));
      if(anyTriggered){sound('alert');toast(`🔔 Price alert triggered on ${symbol}`,"info");}
    }
    // Trailing stop
    if(trailStop){
      const pos=positions.find(p=>p.id===trailStop.posId);
      if(!pos){setTrailStop(null);return;}
      const ts=INSTRUMENTS[pos.symbol]?.tickSize||0.25;
      const dist=trailStop.ticks*ts;
      if(pos.side==='LONG'){
        const hw=Math.max(trailStop.highWater||cur,cur);
        if(cur<=hw-dist){setTrailStop(null);toast(`⚡ Trail stop hit ${fmt.price(cur,pos.symbol)}`,'error');closePos(pos);}
        else if(hw>trailStop.highWater) setTrailStop(t=>({...t,highWater:hw}));
      } else {
        const hw=Math.min(trailStop.highWater||cur,cur);
        if(cur>=hw+dist){setTrailStop(null);toast(`⚡ Trail stop hit ${fmt.price(cur,pos.symbol)}`,'error');closePos(pos);}
        else if(hw<trailStop.highWater) setTrailStop(t=>({...t,highWater:hw}));
      }
    }
    // Break-even stops
    if(beStopIds.size>0){
      beStopIds.forEach(pid=>{
        const pos=positions.find(p=>p.id===pid);
        if(!pos) return;
        const entry=parseFloat(pos.entryPrice);
        if((pos.side==='LONG'&&cur<=entry)||( pos.side==='SHORT'&&cur>=entry)){
          setBeStopIds(s=>{const n=new Set(s);n.delete(pid);return n;});
          toast(`⚡ BE stop triggered — closing`,'info');
          closePos(pos);
        }
      });
    }
  });

  const loadData=useCallback(async()=>{
    try{
      // Step 1: Get account — prefer propAccount id, fall back to fetching list
      let a = propAccount || account;
      if(!a?.id){
        const accts=await accounts.list().catch(()=>[]);
        if(!accts?.length) return;
        a = accts.find(x=>x.status==='FUNDED')||accts.find(x=>x.status==='EVALUATION')||accts[0];
      }
      if(!a?.id) return;

      // Step 2: Always re-fetch the full account object so balance/dailyPnl/drawdown are live
      const [freshAccount, pos, h] = await Promise.all([
        (accounts.get ? accounts.get(a.id) : accounts.list().then(list=>list?.find(x=>x.id===a.id)||a)).catch(()=>a),
        tradesApi.getPositions(a.id).catch(()=>[]),
        tradesApi.list({accountId:a.id,status:'CLOSED',limit:100}).catch(()=>[]),
      ]);

      setAccount(freshAccount || a);
      setPositions(Array.isArray(pos)?pos:[]);
      const hl=h?.trades??h;
      setHistory(Array.isArray(hl)?hl:[]);

      // Notify parent so dashboard stats also refresh
      if(onAccountRefresh) onAccountRefresh();
    }catch(e){console.warn('loadData error',e);}
  },[propAccount, onAccountRefresh]);

  useEffect(()=>{
    loadData();
    // Auto-refresh positions & account every 5 seconds
    const iv = setInterval(loadData, 5000);
    return () => clearInterval(iv);
  },[]);
  useEffect(()=>{ if(propAccount) setAccount(propAccount); },[propAccount]);

  useEffect(()=>{
    const h=(e)=>{
      if(e.target.tagName==='INPUT'||e.target.tagName==='SELECT') return;
      if(e.key==='b'||e.key==='B') handleOrder('buy');
      if(e.key==='s'&&!e.ctrlKey&&!e.metaKey) handleOrder('sell');
      if(e.key==='f'||e.key==='F') flattenAll();
      if(e.key==='+') setQty(q=>q+1);
      if(e.key==='-') setQty(q=>Math.max(1,q-1));
      if(e.key==='?'||e.key==='/') setShowHotkeys(h=>!h);
      if(e.key==='r'||e.key==='R') setShowReplay(v=>!v);
      if(e.key==='a'||e.key==='A') setShowAnalytics(v=>!v);
      if(e.key==='Escape'){setShowHotkeys(false);setShowReplay(false);setShowAnalytics(false);setShowSettings(false);setChartFullscreen(false);}
    };
    window.addEventListener('keydown',h);
    return()=>window.removeEventListener('keydown',h);
  },[account,positions,qty,symbol]);

  const handleOrder=async(side)=>{
    if(!account?.id){toast('No active account','error');return;}
    const ep=parseFloat((chartPriceRef.current||BASE_PRICES[symbol]||21850).toFixed(4));
    const apiSide=side==='buy'?'LONG':'SHORT';
    const opp=side==='buy'?'SHORT':'LONG';
    const ex=positions.find(p=>p.symbol===symbol&&p.side===opp);
    if(ex){await closePos(ex);return;}
    try{
      await tradesApi.open({accountId:account.id,symbol,side:apiSide,quantity:qty,entryPrice:ep});
      await loadData();
      sound(side); toast(`✓ ${side.toUpperCase()} ${qty} ${symbol} @ ${fmt.price(ep,symbol)}`,side==='buy'?'buy':'sell');
    }catch(err){sound('error');toast(err.message||'Order failed','error');}
  };

  const closePos=async(position)=>{
    setClosingId(position.id);
    const xp=parseFloat((chartPriceRef.current||parseFloat(position.entryPrice)).toFixed(4));
    try{
      const ot=await tradesApi.list({accountId:account.id,status:'OPEN',limit:100}).catch(()=>({trades:[]}));
      const list=(ot?.trades??[]).filter(t=>t.symbol===position.symbol);
      let pnl=0,last=null;
      for(const t of list){last=await tradesApi.close(t.id,xp);pnl+=Number(last?.pnl??0);}
      await loadData();
      if(last?.ruleResult?.breached) toast('⚠ ACCOUNT BREACHED','error');
      else if(last?.ruleResult?.passed) toast('🎉 EVALUATION PASSED!','success');
      else { sound('close'); toast(`CLOSED ${position.symbol} — ${pnl>=0?"+":""}${fmt.usd(pnl)}`,pnl>=0?'success':'error'); }
    }catch(err){toast(err.message||'Close failed','error');}
    finally{setClosingId(null);}
  };

  const flattenAll=async()=>{for(const p of positions) await closePos(p);};

  // All fields come from ClearFund backend account object
  // Normalize field names — backend may use different casing/naming conventions
  const bal    = Number(account?.balance ?? account?.currentBalance ?? account?.startingBalance ?? 0);
  const dPnl   = Number(account?.dailyPnl ?? account?.daily_pnl ?? account?.todayPnl ?? 0);
  const startB = Number(account?.startingBalance ?? account?.startBalance ?? account?.initialBalance ?? account?.accountSize ?? bal);
  const maxDD  = Number(account?.maxDrawdown ?? account?.drawdownLimit ?? account?.maxDrawdownLimit ?? account?.drawdown_limit ?? 0);
  const drawdownUsed = Number(account?.drawdownUsed ?? account?.drawdown_used ?? 0);
  const totalPnl = Number(account?.totalPnl ?? account?.total_pnl ?? account?.realizedPnl ?? 0);
  const tradingDays = Number(account?.tradingDays ?? account?.trading_days ?? account?.activeTradingDays ?? 0);
  // MLL = the floor where account gets stopped — starting balance minus max allowed drawdown
  const mll    = (startB > 0 && maxDD > 0) ? (startB - maxDD) : null;
  // uPnl: use live price when available, else chart price, else entry price (no gain)
  const uPnl   = positions.reduce((s,p)=>{
    const inst = INSTRUMENTS[p.symbol] || INSTRUMENTS.NQ;
    // Use live price if position matches current symbol, else use stored unrealizedPnl from backend
    const isCurrentSym = p.symbol===symbol;
    const lp = isCurrentSym ? (livePrice||chartPriceRef.current||parseFloat(p.entryPrice)) : parseFloat(p.entryPrice);
    const rawPnl = (p.side==='LONG'?lp-parseFloat(p.entryPrice):parseFloat(p.entryPrice)-lp)*Number(p.quantity||1);
    // Backend may already provide unrealizedPnl — prefer it for non-current-symbol positions
    const backendPnl = Number(p.unrealizedPnl ?? p.pnl ?? NaN);
    const pnl = isCurrentSym ? rawPnl*inst.tickValue/inst.tickSize : (isNaN(backendPnl) ? rawPnl*inst.tickValue/inst.tickSize : backendPnl);
    return s + pnl;
  },0);

  const TC={buy:"#00C896",sell:"#FF3B3B",success:"#00C896",error:"#FF3B3B",info:"#555"};

  const handleCtxMenu=(e)=>{e.preventDefault();setCtxMenu({x:e.clientX,y:e.clientY});};

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html,body,#root{height:100%;background:#111318;}
        body{font-family:'Inter',sans-serif;color:#e2e4e9;-webkit-font-smoothing:antialiased;overflow:hidden;}
        ::-webkit-scrollbar{width:4px;height:4px;}
        ::-webkit-scrollbar-track{background:#111318;}
        ::-webkit-scrollbar-thumb{background:#2a2d35;border-radius:2px;}
        ::-webkit-scrollbar-thumb:hover{background:#3a3d47;}
        @keyframes slideIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes alertPulse{0%,100%{box-shadow:0 0 6px rgba(167,139,250,0.4)}50%{box-shadow:0 0 18px rgba(167,139,250,0.9)}}
        .drag-ew *{cursor:ew-resize!important;user-select:none!important;}
        .drag-ns *{cursor:ns-resize!important;user-select:none!important;}
        .tbl-row:hover td{background:rgba(255,255,255,0.03)!important;}
        .nav-icon{display:flex;align-items:center;justify-content:center;width:40px;height:40px;cursor:pointer;color:#4a4f5e;border-radius:6px;transition:all 0.15s;flex-shrink:0;}
        .nav-icon:hover{color:#e2e4e9;background:rgba(255,255,255,0.06);}
        .nav-icon.active{color:#00c896;background:rgba(0,200,150,0.1);}
        .tab-btn{padding:0 16px;height:36px;display:flex;align-items:center;font-size:12px;font-weight:500;color:#5a5f6e;cursor:pointer;border-bottom:2px solid transparent;transition:all 0.15s;white-space:nowrap;}
        .tab-btn:hover{color:#a0a5b4;}
        .tab-btn.active{color:#e2e4e9;border-bottom-color:#00c896;}
        .order-btn{width:100%;padding:10px;font-size:12px;font-weight:600;border:none;border-radius:4px;cursor:pointer;letter-spacing:0.02em;transition:all 0.15s;}
        .tf-btn{padding:3px 8px;font-size:11px;font-weight:500;background:none;border:1px solid transparent;color:#5a5f6e;cursor:pointer;border-radius:3px;transition:all 0.1s;}
        .tf-btn:hover{color:#a0a5b4;background:rgba(255,255,255,0.05);}
        .tf-btn.active{color:#00c896;border-color:rgba(0,200,150,0.4);background:rgba(0,200,150,0.08);}
        select option{background:#1a1d27;}
        input:focus{outline:none;}
        .qty-chip{padding:4px 0;background:#1e2130;border:1px solid #2a2d3a;color:#7a7f8e;font-size:11px;font-weight:600;border-radius:3px;cursor:pointer;text-align:center;transition:all 0.1s;}
        .qty-chip:hover{border-color:#3a3d4a;color:#a0a5b4;}
        .qty-chip.active{background:rgba(0,200,150,0.1);border-color:rgba(0,200,150,0.5);color:#00c896;}
      `}</style>

      <div style={{display:"flex",height:"100vh",background:"#111318",overflow:"hidden"}} onClick={e=>{ if(showSymSearch&&!e.target.closest('[data-sym-search]')) setShowSymSearch(false); }}>

        {/* ── LEFT ICON NAV ── */}
        <div style={{width:56,flexShrink:0,background:"#0d0f14",borderRight:"1px solid #1e2130",display:"flex",flexDirection:"column",alignItems:"center",paddingTop:8,gap:2,zIndex:10}}>
          {/* Logo */}
          <div style={{width:32,height:32,background:"linear-gradient(135deg,#00c896,#00a07a)",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8,flexShrink:0}}>
            <span style={{color:"#000",fontWeight:800,fontSize:11}}>CF</span>
          </div>
          {[
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,            label:"Chart",       action:null,                              panel:null},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,label:"Watchlist",action:null,panel:"watchlist"},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="9" height="9" rx="1"/><rect x="12" y="3" width="9" height="9" rx="1"/><rect x="3" y="12" width="9" height="9" rx="1"/><rect x="12" y="12" width="9" height="9" rx="1"/></svg>, label:"Dashboard",            action:()=>{ if(onNavigate) onNavigate("dashboard");   else window.dispatchEvent(new CustomEvent('nav:page',{detail:'dashboard'}));   }, panel:null},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,             label:"Performance Calendar", action:null, panel:"calendar"},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,                                       label:"Performance",           action:()=>{ if(onNavigate) onNavigate("performance"); else window.dispatchEvent(new CustomEvent('nav:page',{detail:'performance'})); }, panel:null},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,              label:"Support",               action:()=>{ if(onNavigate) onNavigate("support");      else window.dispatchEvent(new CustomEvent('nav:page',{detail:'support'}));      }, panel:null},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, label:"Price Alerts", action:null, panel:"alerts"},
            {svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="17" width="18" height="4" rx="1"/></svg>, label:"DOM Ladder", action:null, panel:"dom"},
          ].map((n,i)=>(
            <div key={i}
              className={`nav-icon${(i===0&&!sidePanel)||sidePanel===n.panel&&n.panel?' active':''}`}
              title={n.label}
              onClick={()=>{
                if(n.panel) setSidePanel(p=>p===n.panel?null:n.panel);
                else if(n.action) n.action();
              }}
            >{n.svg}</div>
          ))}
          <div style={{flex:1}}/>
          <div className="nav-icon" title="Trade Replay (R)" onClick={()=>setShowReplay(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </div>
          <div className="nav-icon" title="Analytics Dashboard (A)" onClick={()=>setShowAnalytics(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
          </div>
          <div className="nav-icon" title="Chart Settings" onClick={()=>setShowSettings(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M12 2v2m0 16v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M2 12h2m16 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
          </div>
          <div style={{height:8}}/>
        </div>

        {/* ── MAIN AREA ── */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>

          {/* ── TOP BAR ── */}
          <div style={{height:38,flexShrink:0,background:"#0d0f14",borderBottom:"1px solid #1e2130",display:"flex",alignItems:"center",padding:"0 12px",gap:0}}>
            {/* Account pill */}
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"4px 10px",background:"#1a1d27",borderRadius:4,border:"1px solid #2a2d3a",marginRight:12,cursor:"pointer"}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:"#00c896",display:"inline-block",animation:"blink 2s infinite",flexShrink:0}}/>
              <span style={{fontSize:11,fontWeight:600,color:"#e2e4e9",letterSpacing:"0.02em"}}>{account?.name||"$150K PRACTICE"}</span>
              <span style={{fontSize:9,color:"#4a4f5e"}}>▼</span>
            </div>
            {/* Stats — sourced from CashBalanceSnapshot + UserAccountAutoLiq */}
            {[
              {l:"BAL",  v:bal>0?fmt.usd(bal):"—",                    c:"#e2e4e9"},
              {l:"MLL",  v:mll!=null?fmt.usd(mll):"—",                c:"#f03b3b"},
              {l:"RP&L", v:(dPnl>=0?"+":"")+fmt.usd(dPnl),            c:dPnl>=0?"#00c896":"#f03b3b"},
              {l:"UP&L", v:(uPnl>=0?"+":"")+fmt.usd(uPnl),            c:uPnl>=0?"#00c896":"#f03b3b"},
              {l:"TRADES",v:positions.length>0?`${positions.length} open`:"Flat",c:positions.length?"#f5a623":"#4a4f5e"},
            ].map(m=>(
              <div key={m.l} style={{display:"flex",alignItems:"center",gap:4,padding:"0 9px",borderRight:"1px solid #1e2130",height:44,flexShrink:1,minWidth:0}}>
                <span style={{fontSize:11,color:"#4a4f5e",fontWeight:500,whiteSpace:"nowrap"}}>{m.l}:</span>
                <span style={{fontSize:12,fontWeight:600,color:m.c,fontFamily:"monospace",whiteSpace:"nowrap"}}>{m.v}</span>
              </div>
            ))}
            {/* Goal editor */}
            <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 9px",borderRight:"1px solid #1e2130",height:44,flexShrink:0}}>
              <span style={{fontSize:9,color:"#4a4f5e"}}>GOAL $</span>
              <input type="number" value={dailyGoal} min={0} step={100}
                onChange={e=>setDailyGoal(Math.max(0,parseInt(e.target.value)||0))}
                style={{width:48,background:"#111318",border:"1px solid #2a2d3a",color:"#f5a623",
                  borderRadius:3,fontSize:11,padding:"2px 4px",fontFamily:"monospace",
                  textAlign:"right",fontWeight:600}}/>
            </div>
            <div style={{flex:1}}/>
            {/* Connection + clock */}
            <div style={{display:"flex",alignItems:"center",gap:5,padding:"0 10px",borderLeft:"1px solid #1e2130",height:44,flexShrink:0}}>
              <span style={{fontSize:9,color:"#4a4f5e"}}>Refresh Rate:</span>
              <span style={{fontSize:9,color:mdConnected?"#00c896":"#f5a623",fontWeight:600}}>{mdConnected?"LIVE":"SIM"}</span>
              <span style={{width:5,height:5,borderRadius:"50%",background:mdConnected?"#00c896":"#f5a623",animation:"blink 1.5s infinite"}}/>
            </div>
            <div style={{padding:"0 10px",borderLeft:"1px solid #1e2130",height:44,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              <span style={{fontSize:12,color:"#5a5f6e",fontFamily:"monospace"}}><Clock/></span>
              <button onClick={()=>setShowHotkeys(h=>!h)} title="Keyboard Shortcuts (?)"
                style={{background:"none",border:"1px solid #2a2d3a",color:"#4a4f5e",borderRadius:3,width:24,height:24,
                  cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,flexShrink:0}}>?</button>
              <button onClick={()=>setSoundEnabled(s=>!s)} title={soundEnabled?"Sound On (click to mute)":"Sound Off (click to enable)"}
                style={{background:soundEnabled?"rgba(0,200,150,0.08)":"none",border:"1px solid",borderColor:soundEnabled?"rgba(0,200,150,0.3)":"#2a2d3a",
                  color:soundEnabled?"#00c896":"#3a3d4a",borderRadius:3,width:20,height:20,
                  cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,flexShrink:0}}>
                {soundEnabled?"♪":"♪"}
              </button>
              {alerts.filter(a=>!a.triggered).length>0&&(
                <div title="Active alerts" style={{background:"rgba(167,139,250,0.12)",border:"1px solid rgba(167,139,250,0.3)",borderRadius:3,padding:"1px 6px",fontSize:10,color:"#a78bfa",cursor:"pointer",whiteSpace:"nowrap"}}
                  onClick={()=>setSidePanel(p=>p==='alerts'?null:'alerts')}>
                  🔔 {alerts.filter(a=>!a.triggered).length}
                </div>
              )}
            </div>
          </div>

          {/* ── DAILY RISK / GOAL BAR ── */}
          {showRiskBar&&(()=>{
            const pnlNow=dPnl+uPnl;
            const goalPct=dailyGoal>0?Math.min(100,Math.max(0,(pnlNow/dailyGoal)*100)):0;
            const maxLoss=mll!=null?bal-mll:null;
            const lossPct=maxLoss>0&&bal>0?Math.min(100,Math.max(0,(-Math.min(0,pnlNow)/maxLoss)*100)):0;
            const nearLimit=lossPct>70;
            return (
              <div style={{flexShrink:0,background:"#0d0f14",borderBottom:"1px solid #1e2130",padding:"4px 12px",display:"flex",gap:12,alignItems:"center"}}>
                <div style={{flex:1,minWidth:100}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontSize:9,color:"#4a4f5e",fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase"}}>Daily Goal</span>
                    <span style={{fontSize:9,fontFamily:"monospace",color:pnlNow>=dailyGoal?"#00c896":pnlNow>0?"#f5a623":"#5a5f6e"}}>{pnlNow>=0?"+":""}{fmt.usd(pnlNow)} / {fmt.usd(dailyGoal)}</span>
                  </div>
                  <div style={{height:3,background:"#1e2130",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${goalPct}%`,background:pnlNow>=dailyGoal?"#00c896":"#f5a623",borderRadius:2,transition:"width 0.4s"}}/>
                  </div>
                </div>
                {maxLoss!=null&&(
                  <div style={{flex:1,minWidth:100}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                      <span style={{fontSize:9,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",color:nearLimit?"#f03b3b":"#4a4f5e"}}>{nearLimit?"⚠ ":""}Max Loss</span>
                      <span style={{fontSize:9,fontFamily:"monospace",color:nearLimit?"#f03b3b":"#5a5f6e"}}>{fmt.usd(Math.max(0,-pnlNow))} / {fmt.usd(maxLoss)}</span>
                    </div>
                    <div style={{height:3,background:"#1e2130",borderRadius:2,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${lossPct}%`,background:nearLimit?"#f03b3b":"#e65100",borderRadius:2,transition:"width 0.4s"}}/>
                    </div>
                  </div>
                )}
                <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                  <span style={{fontSize:9,color:"#3a3d4a"}}>Target $</span>
                  <input type="number" value={dailyGoal} min={0} step={100}
                    onChange={e=>setDailyGoal(Math.max(0,parseInt(e.target.value)||0))}
                    style={{width:55,background:"#1a1d27",border:"1px solid #2a2d3a",color:"#7a7f8e",
                      borderRadius:3,fontSize:10,padding:"2px 5px",fontFamily:"monospace",textAlign:"right"}}/>
                  <button onClick={()=>setShowRiskBar(false)} title="Hide bar"
                    style={{background:"none",border:"none",color:"#2a2d3a",cursor:"pointer",fontSize:13,lineHeight:1,padding:0}}>✕</button>
                </div>
              </div>
            );
          })()}

          {/* ── BODY (chart area + right panel) ── */}
          <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0,position:"relative"}}>

            {/* ── CHART COLUMN ── */}
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0,
              ...(chartFullscreen?{position:"fixed",inset:0,zIndex:9500,background:"#0d0f14",display:"flex",flexDirection:"column"}:{})}}>

              {/* Chart toolbar */}
              <div style={{height:36,flexShrink:0,background:"#141720",borderBottom:"1px solid #1e2130",display:"flex",alignItems:"center",padding:"0 8px",gap:6}}>
                {/* Symbol search */}
                <div style={{position:"relative",marginRight:4}}>
                  <div
                    onClick={()=>{setShowSymSearch(s=>!s);setSymQuery('');}}
                    style={{display:"flex",alignItems:"center",gap:6,background:"#1a1d27",border:`1px solid ${showSymSearch?"#00c896":"#2a2d3a"}`,borderRadius:4,padding:"3px 8px",cursor:"pointer",minWidth:90}}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#5a5f6e" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    {showSymSearch
                      ? <input autoFocus value={symQuery} onChange={e=>setSymQuery(e.target.value)}
                          onKeyDown={e=>{
                            if(e.key==='Escape'){setShowSymSearch(false);setSymQuery('');}
                            const matches=Object.keys(INSTRUMENTS).filter(s=>s.toLowerCase().includes(symQuery.toLowerCase())||INSTRUMENTS[s].name.toLowerCase().includes(symQuery.toLowerCase()));
                            if(e.key==='Enter'&&matches.length){setSymbol(matches[0]);chartPriceRef.current=null;setShowSymSearch(false);setSymQuery('');}
                          }}
                          style={{width:80,background:"none",border:"none",color:"#e2e4e9",fontSize:12,fontWeight:600,outline:"none"}}
                          placeholder="Search..."
                        />
                      : <span style={{fontSize:12,fontWeight:600,color:"#e2e4e9"}}>{symbol}</span>
                    }
                    <span style={{fontSize:9,color:"#3a3d4a"}}>▼</span>
                  </div>
                  {/* Dropdown */}
                  {showSymSearch&&(
                    <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,width:260,background:"#1a1d27",border:"1px solid #2a2d3a",borderRadius:6,zIndex:200,boxShadow:"0 8px 32px rgba(0,0,0,0.6)",overflow:"hidden",maxHeight:320,overflowY:"auto"}}>
                      {CATEGORIES.map(cat=>{
                        const syms=Object.entries(INSTRUMENTS).filter(([s,v])=>
                          v.category===cat&&(
                            !symQuery||s.toLowerCase().includes(symQuery.toLowerCase())||v.name.toLowerCase().includes(symQuery.toLowerCase())
                          )
                        );
                        if(!syms.length) return null;
                        return <div key={cat}>
                          <div style={{padding:"5px 10px",fontSize:9,color:"#3a3d4a",fontWeight:700,letterSpacing:"0.12em",background:"#111318",borderBottom:"1px solid #1e2130",textTransform:"uppercase"}}>{cat}</div>
                          {syms.map(([s,info])=>(
                            <div key={s}
                              onClick={()=>{setSymbol(s);chartPriceRef.current=null;setShowSymSearch(false);setSymQuery('');}}
                              style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px",cursor:"pointer",background:s===symbol?"rgba(0,200,150,0.08)":"transparent",borderBottom:"1px solid rgba(255,255,255,0.03)"}}
                              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                              onMouseLeave={e=>e.currentTarget.style.background=s===symbol?"rgba(0,200,150,0.08)":"transparent"}
                            >
                              <div>
                                <span style={{fontSize:12,fontWeight:700,color:s===symbol?"#00c896":"#e2e4e9",fontFamily:"monospace"}}>{s}</span>
                                <span style={{fontSize:10,color:"#4a4f5e",marginLeft:8}}>{info.name}</span>
                              </div>
                              <span style={{fontSize:9,color:"#3a3d4a"}}>{info.exchange}</span>
                            </div>
                          ))}
                        </div>;
                      })}
                    </div>
                  )}
                </div>
                {/* Timeframes */}
                {TIMEFRAMES.slice(0,10).map(tf=>(
                  <button key={tf} className={`tf-btn${timeframe===tf?' active':''}`} onClick={()=>setTimeframe(tf)}>{tf}</button>
                ))}
                <div style={{position:"relative"}}>
                  <button className={`tf-btn${TIMEFRAMES.slice(10).includes(timeframe)?' active':''}`}
                    onClick={e=>{e.stopPropagation();const m=document.getElementById('tfmenu');m.style.display=m.style.display==='block'?'none':'block';}}>
                    {TIMEFRAMES.slice(10).includes(timeframe)?timeframe:'more ▾'}
                  </button>
                  <div id="tfmenu" style={{display:'none',position:'absolute',top:'100%',left:0,zIndex:999,
                    background:'#1a1d27',border:'1px solid #2a2d3a',borderRadius:4,minWidth:64,boxShadow:'0 8px 24px rgba(0,0,0,0.7)',padding:'3px 0'}}>
                    {TIMEFRAMES.slice(10).map(tf=>(
                      <div key={tf} onClick={()=>{setTimeframe(tf);document.getElementById('tfmenu').style.display='none';}}
                        style={{padding:'6px 14px',fontSize:11,color:timeframe===tf?'#00c896':'#a0a5b4',cursor:'pointer',whiteSpace:'nowrap'}}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{tf}</div>
                    ))}
                  </div>
                </div>
                <CandleTimer timeframe={timeframe}/>
                <div style={{flex:1}}/>
                <button onClick={()=>setChartFullscreen(f=>!f)} title="Full-screen chart (Shift+F)"
                  style={{width:26,height:26,background:"none",border:"none",color:"#3a3d4a",cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,borderRadius:3}}
                  onMouseEnter={e=>e.currentTarget.style.color="#7a7f8e"}
                  onMouseLeave={e=>e.currentTarget.style.color="#3a3d4a"}>
                  {chartFullscreen
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/></svg>
                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                  }
                </button>
                <div style={{width:1,height:20,background:"#1e2130",margin:"0 2px"}}/>
                {/* Drawing tools */}
                {[
                  {t:'hline',    icon:'—', tip:'Horizontal Line (H)'},
                  {t:'trendline', icon:'╱', tip:'Trendline (T)'},
                  {t:'fib',       icon:'φ', tip:'Fibonacci Retracement (F)'},
                  {t:'rect',      icon:'▭', tip:'Rectangle (R)'},
                ].map(({t,icon,tip})=>(
                  <button key={t} title={tip} onClick={()=>setActiveTool(at=>at===t?null:t)} style={{
                    width:28,height:26,background:activeTool===t?"rgba(0,200,150,0.12)":"none",
                    border:activeTool===t?"1px solid rgba(0,200,150,0.4)":"1px solid transparent",
                    color:activeTool===t?"#00c896":"#5a5f6e",cursor:"pointer",borderRadius:3,
                    fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.1s",
                  }}>{icon}</button>
                ))}
                <button title="Clear drawings (D)" onClick={()=>drawingsRef.current?.clear()} style={{width:28,height:26,background:"none",border:"1px solid transparent",color:"#5a5f6e",cursor:"pointer",borderRadius:3,fontSize:11,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                <div style={{width:1,height:20,background:"#1e2130",margin:"0 4px"}}/>
                {/* Indicators button */}
                <button title="Indicators" onClick={()=>setShowIndicators(s=>!s)} style={{
                  height:26,padding:"0 10px",background:showIndicators?"rgba(0,200,150,0.12)":"none",
                  border:showIndicators?"1px solid rgba(0,200,150,0.4)":"1px solid transparent",
                  color:showIndicators?"#00c896":"#5a5f6e",cursor:"pointer",borderRadius:3,
                  fontSize:10,fontWeight:600,display:"flex",alignItems:"center",gap:4,transition:"all 0.1s",
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  Indicators
                  {indicators.filter(i=>i.visible).length>0&&<span style={{background:"rgba(0,200,150,0.2)",borderRadius:10,padding:"0 5px",fontSize:9,color:"#00c896"}}>{indicators.filter(i=>i.visible).length}</span>}
                </button>
                <div style={{flex:1}}/>
                {/* OHLC */}
                {chartSettings.showOHLC&&<OHLCBar symbol={symbol} priceRef={chartPriceRef} settings={chartSettings}/>}
                <div style={{padding:"0 8px",fontSize:10,fontFamily:"monospace",display:"flex",alignItems:"center",gap:4}}>
                  <span style={{
                    display:"inline-block",width:6,height:6,borderRadius:"50%",
                    background:mdConnected?"#00c896":"#4a4f5e",
                    boxShadow:mdConnected?"0 0 6px #00c896":undefined,
                    animation:mdConnected?"blink 2s ease-in-out infinite":undefined,
                  }}/>
                  <span style={{color:mdConnected?"#00c896":"#4a4f5e"}}>{mdConnected?"LIVE":"SIM"}</span>
                </div>
              </div>

              {/* Indicators Panel */}
              {showIndicators&&(
                <div style={{background:"#141720",borderBottom:"1px solid #1e2130",padding:"8px 12px",flexShrink:0}}>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,alignItems:"center"}}>
                    <span style={{fontSize:9,fontWeight:700,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginRight:4}}>Indicators</span>
                    {indicators.map(ind=>(
                      <div key={ind.id} style={{
                        display:"flex",alignItems:"center",gap:5,padding:"3px 8px",
                        background:ind.visible?"rgba(0,0,0,0.25)":"transparent",
                        border:`1px solid ${ind.visible?ind.color+"55":"#2a2d3a"}`,
                        borderRadius:3,cursor:"pointer",transition:"all 0.1s",position:"relative",
                      }}
                        onClick={()=>setIndicators(is=>is.map(i=>i.id===ind.id?{...i,visible:!i.visible}:i))}
                        onContextMenu={e=>{e.preventDefault();setIndicators(is=>is.filter(i=>i.id!==ind.id));}}
                        title={`Click to toggle · Right-click to remove`}>
                        <div style={{width:12,height:2,background:ind.visible?ind.color:"#3a3d4a",borderRadius:1,flexShrink:0}}/>
                        <span style={{fontSize:10,fontWeight:600,color:ind.visible?ind.color:"#4a4f5e",fontFamily:"monospace"}}>{ind.type}{ind.period}</span>
                      </div>
                    ))}
                    <div style={{width:1,height:16,background:"#1e2130",margin:"0 2px"}}/>
                    {/* Add custom indicator */}
                    {(()=>{
                      const [adding,setAdding] = [null,null]; // placeholder - use simple inline form
                      return null;
                    })()}
                    {/* Inline add form */}
                    <AddIndicatorForm onAdd={ind=>setIndicators(is=>[...is,{...ind,id:Date.now(),visible:true}])}/>
                    <button onClick={()=>setIndicators(is=>is.map(i=>({...i,visible:false})))}
                      style={{marginLeft:"auto",padding:"3px 8px",background:"none",border:"1px solid #2a2d3a",color:"#4a4f5e",borderRadius:3,fontSize:10,cursor:"pointer"}}>Hide All</button>
                  </div>
                </div>
              )}

              {/* Chart */}
              <div style={{flex:1,position:"relative",overflow:"hidden",minHeight:0,background:chartSettings.bgColor}}>
                <CandleChart symbol={symbol} timeframe={timeframe} livePrice={livePrice} priceRef={chartPriceRef}
                  settings={chartSettings} onContextMenu={handleCtxMenu}
                  positions={chartSettings.showPositionLines?positions:[]}
                  activeTool={activeTool} setActiveTool={setActiveTool} drawingsRef={drawingsRef}
                  priceAlerts={alerts.filter(a=>a.symbol===symbol&&!a.triggered)}
                  indicators={indicators}
                  economicEvents={chartEvents}/>
              </div>

              {/* ── RSI SUB-PANEL ── */}
              {indicators.some(i=>i.type==='RSI'&&i.visible)&&(
                <RSIPanel candles={null} indicators={indicators} symbol={symbol}/>
              )}

              {/* ── ECONOMIC CALENDAR STRIP ── */}
              <EconomicCalendarStrip
                enabledTypes={enabledEventTypes}
                onToggleType={key => setEnabledEventTypes(t =>
                  t.includes(key) ? t.filter(k=>k!==key) : [...t, key]
                )}
              />

              {/* ── DRAG HANDLE — bottom panel ── */}
              <div
                onMouseDown={e=>{
                  e.preventDefault();
                  document.body.style.cursor='ns-resize';
                  document.body.style.userSelect='none';
                  document.body.classList.add('drag-ns');
                  draggingRef.current={type:'bottom',startY:e.clientY,startH:bottomH};
                }}
                style={{height:5,flexShrink:0,cursor:"ns-resize",background:"transparent",borderTop:"1px solid #1e2130",position:"relative",zIndex:10,display:"flex",alignItems:"center",justifyContent:"center"}}
                onMouseEnter={e=>e.currentTarget.style.borderTopColor="#00c896"}
                onMouseLeave={e=>{ if(!draggingRef.current) e.currentTarget.style.borderTopColor="#1e2130"; }}
              >
                <div style={{width:36,height:2,borderRadius:1,background:"#2a2d3a",pointerEvents:"none"}}/>
              </div>

              {/* ── BOTTOM PANEL — Positions / History ── */}
              <div style={{height:bottomH,flexShrink:0,background:"#0d0f14",display:"flex",flexDirection:"column",overflow:"hidden",borderTop:"1px solid #1e2130"}}>
                {/* Tabs */}
                <div style={{display:"flex",alignItems:"center",background:"#111318",borderBottom:"1px solid #1e2130",flexShrink:0,padding:"0 8px"}}>
                  {[
                    {k:"positions",l:`Positions (${positions.length})`},
                    {k:"history",l:`Trades (${history.length})`},
                    {k:"stats",l:"Stats"},
                    {k:"journal",l:"Journal"},
                    {k:"orders",l:"Orders (0)"},
                  ].map(t=>(
                    <div key={t.k} className={`tab-btn${tab===t.k?' active':''}`} onClick={()=>setTab(t.k)}>{t.l}</div>
                  ))}
                  <div style={{flex:1}}/>
                  {positions.length>0&&(
                    <button onClick={flattenAll} style={{padding:"4px 14px",background:"rgba(240,59,59,0.1)",border:"1px solid rgba(240,59,59,0.3)",color:"#f03b3b",borderRadius:3,fontSize:11,fontWeight:600,cursor:"pointer",marginRight:8}}>⚡ Flatten All</button>
                  )}
                  <button onClick={loadData} style={{padding:"4px 8px",background:"none",border:"1px solid #2a2d3a",color:"#5a5f6e",borderRadius:3,fontSize:11,cursor:"pointer",marginRight:4}}>↺</button>
                  {tab==="history"&&history.length>0&&(
                    <button onClick={()=>{const r=[["Symbol","Side","Qty","Entry","Exit","P&L","Time"],...history.map(t=>[t.symbol,t.side,t.quantity,parseFloat(t.entryPrice).toFixed(2),t.exitPrice?parseFloat(t.exitPrice).toFixed(2):'',Number(t.pnl).toFixed(2),t.closedAt?new Date(t.closedAt).toLocaleString():''])];const a=document.createElement("a");a.href="data:text/csv,"+encodeURIComponent(r.map(x=>x.join(",")).join("\n"));a.download="trades.csv";a.click();}}
                      style={{padding:"4px 10px",background:"#1a1d27",border:"1px solid #2a2d3a",color:"#7a7f8e",borderRadius:3,fontSize:11,cursor:"pointer",marginRight:4}}>↓ Export</button>
                  )}
                </div>
                {/* Table */}
                <div style={{overflowY:"auto",flex:1}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr style={{background:"#111318",position:"sticky",top:0,zIndex:1}}>
                        {(tab==="positions"
                          ? ["Symbol","Side","Qty","Entry Price","Current","Unreal. P&L","P&L %","Action"]
                          : tab==="history"
                          ? ["Symbol","Side","Qty","Entry","Exit","P&L","% Return","Time"]
                          : tab==="stats"
                          ? []
                          : ["Symbol","Type","Qty","Price","Status","Time"]
                        ).map(h=>(
                          <th key={h} style={{padding:"6px 10px",color:"#5a5f6e",fontSize:10,fontWeight:500,letterSpacing:"0.06em",borderBottom:"1px solid #1e2130",textAlign:"left",whiteSpace:"nowrap",textTransform:"uppercase"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tab==="positions"&&<>
                        {!positions.length&&<tr><td colSpan={7} style={{textAlign:"center",color:"#2a2d3a",padding:"20px 0",fontSize:11}}>No active positions</td></tr>}
                        {positions.map(p=>{
                          const c=chartPriceRef.current||parseFloat(p.entryPrice),e=parseFloat(p.entryPrice);
                          const u=(p.side==='LONG'?c-e:e-c)*Number(p.quantity);
                          return <tr key={p.id} className="tbl-row" style={{borderBottom:"1px solid #1a1d27"}}>
                            <td style={{padding:"6px 10px",fontWeight:600,color:"#e2e4e9",fontFamily:"monospace"}}>{p.symbol}</td>
                            <td style={{padding:"6px 10px",color:p.side==="LONG"?"#00c896":"#f03b3b",fontWeight:600}}>{p.side}</td>
                            <td style={{padding:"6px 10px",color:"#a0a5b4",fontFamily:"monospace"}}>{p.quantity}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",color:"#a0a5b4"}}>{e.toFixed(2)}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",color:"#7a7f8e"}}>{c.toFixed(2)}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",fontWeight:700,color:u>=0?"#00c896":"#f03b3b",
                              textShadow:u>50?"0 0 8px rgba(0,200,150,0.5)":u<-50?"0 0 8px rgba(240,59,59,0.5)":undefined}}>
                              {u>=0?"+":""}{fmt.usd(u)}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",fontSize:10,color:u>=0?"#00c896":"#f03b3b"}}>
                              {(()=>{const inst2=INSTRUMENTS[p.symbol];if(!inst2||!e)return"—";const ticks=Math.round(Math.abs(curP-e)/inst2.tickSize)*(u>=0?1:-1);return(ticks>=0?"+":"")+ticks+"t";})()}
                            </td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",fontSize:10,color:u>=0?"#00c896":"#f03b3b"}}>{e>0?((u/(e*Number(p.quantity)))*100).toFixed(2)+"%":"—"}</td>
                            <td style={{padding:"6px 10px"}}><button onClick={()=>closePos(p)} disabled={closingId===p.id} style={{padding:"3px 12px",background:"rgba(240,59,59,0.1)",border:"1px solid rgba(240,59,59,0.3)",color:"#f03b3b",borderRadius:3,fontSize:10,fontWeight:600,cursor:"pointer"}}>{closingId===p.id?"...":"Close"}</button></td>
                          </tr>;
                        })}
                      </>}
                      {tab==="history"&&<>
                        {!history.length&&<tr><td colSpan={7} style={{textAlign:"center",color:"#2a2d3a",padding:"20px 0",fontSize:11}}>No trade history</td></tr>}
                        {history.map(t=>{
                          const p=Number(t.pnl);
                          return <tr key={t.id} className="tbl-row" style={{borderBottom:"1px solid #1a1d27"}}>
                            <td style={{padding:"6px 10px",fontWeight:600,color:"#e2e4e9",fontFamily:"monospace"}}>{t.symbol}</td>
                            <td style={{padding:"6px 10px",color:t.side==="LONG"?"#00c896":"#f03b3b",fontWeight:600}}>{t.side}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",color:"#a0a5b4"}}>{t.quantity}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",color:"#a0a5b4"}}>{parseFloat(t.entryPrice).toFixed(2)}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",color:"#a0a5b4"}}>{t.exitPrice?parseFloat(t.exitPrice).toFixed(2):"—"}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",fontWeight:700,color:p>=0?"#00c896":"#f03b3b"}}>{p>=0?"+":""}{fmt.usd(p)}</td>
                            <td style={{padding:"6px 10px",fontFamily:"monospace",fontSize:10,color:p>=0?"#00c896":"#f03b3b"}}>{t.entryPrice&&t.exitPrice?((Math.abs(p)/(parseFloat(t.entryPrice)*Number(t.quantity||1)))*100*(p>=0?1:-1)).toFixed(2)+"%":"—"}</td>
                            <td style={{padding:"6px 10px",color:"#5a5f6e",fontSize:10}}>{t.closedAt?new Date(t.closedAt).toLocaleTimeString():"—"}</td>
                          </tr>;
                        })}
                      </>}
                      {tab==="stats"&&(
                        <tr><td colSpan={7} style={{padding:0}}>
                          <div style={{padding:"12px 16px"}}>
                            {(()=>{
                              const closed=history.filter(t=>t.exitPrice);
                              const wins=closed.filter(t=>Number(t.pnl)>0);
                              const losses=closed.filter(t=>Number(t.pnl)<0);
                              const totalPnl=closed.reduce((s,t)=>s+Number(t.pnl),0);
                              const avgWin=wins.length?wins.reduce((s,t)=>s+Number(t.pnl),0)/wins.length:0;
                              const avgLoss=losses.length?losses.reduce((s,t)=>s+Number(t.pnl),0)/losses.length:0;
                              const grossWin=wins.reduce((s,t)=>s+Number(t.pnl),0);
                              const grossLoss=Math.abs(losses.reduce((s,t)=>s+Number(t.pnl),0));
                              const pf=grossLoss>0?grossWin/grossLoss:grossWin>0?Infinity:0;
                              const winRate=closed.length?wins.length/closed.length*100:0;
                              const bestTrade=closed.length?Math.max(...closed.map(t=>Number(t.pnl))):0;
                              const worstTrade=closed.length?Math.min(...closed.map(t=>Number(t.pnl))):0;
                              const rrRatio=avgLoss!==0?Math.abs(avgWin/avgLoss):0;
                              const stats=[
                                {l:"Total Trades",v:closed.length,c:"#e2e4e9"},
                                {l:"Win Rate",v:closed.length?winRate.toFixed(1)+"%":"—",c:winRate>=50?"#00c896":"#f03b3b"},
                                {l:"Profit Factor",v:pf===Infinity?"∞":pf.toFixed(2),c:pf>=1.5?"#00c896":pf>=1?"#f5a623":"#f03b3b"},
                                {l:"Total P&L",v:(totalPnl>=0?"+":"")+fmt.usd(totalPnl),c:totalPnl>=0?"#00c896":"#f03b3b"},
                                {l:"Avg Win",v:avgWin?"+"+fmt.usd(avgWin):"—",c:"#00c896"},
                                {l:"Avg Loss",v:avgLoss?fmt.usd(avgLoss):"—",c:"#f03b3b"},
                                {l:"R:R Ratio",v:rrRatio?rrRatio.toFixed(2)+"R":"—",c:rrRatio>=2?"#00c896":rrRatio>=1?"#f5a623":"#f03b3b"},
                                {l:"Best Trade",v:bestTrade?("+"+fmt.usd(bestTrade)):"—",c:"#00c896"},
                                {l:"Worst Trade",v:worstTrade?fmt.usd(worstTrade):"—",c:"#f03b3b"},
                                {l:"Sharpe",v:sharpe,c:parseFloat(sharpe)>=1?"#00c896":parseFloat(sharpe)>=0?"#f5a623":"#f03b3b"},
                                {l:"Win Streak",v:maxWinStreak,c:"#00c896"},
                                {l:"Loss Streak",v:maxLossStreak,c:"#f03b3b"},
                                {l:"Avg Hold",v:avgHold>0?fmtHold(avgHold):"—",c:"#a0a5b4"},
                                {l:"Winners",v:wins.length,c:"#00c896"},
                                {l:"Losers",v:losses.length,c:"#f03b3b"},
                                {l:"Gross Profit",v:"+"+fmt.usd(grossWin),c:"#00c896"},
                              ];
                              return (
                                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"6px 24px"}}>
                                  {stats.map(s=>(
                                    <div key={s.l} style={{padding:"6px 0",borderBottom:"1px solid #1a1d27"}}>
                                      <div style={{fontSize:9,color:"#4a4f5e",fontWeight:500,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:3}}>{s.l}</div>
                                      <div style={{fontSize:13,fontWeight:700,color:s.c,fontFamily:"monospace"}}>{s.v}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            {/* Equity curve sparkline */}
                            {(()=>{
                              const closed=history.filter(t=>t.exitPrice&&t.pnl!=null);
                              if(closed.length < 2) return null;
                              const cumPnl=[];
                              let running=0;
                              closed.forEach(t=>{running+=Number(t.pnl);cumPnl.push(running);});
                              const minVal=Math.min(...cumPnl), maxVal=Math.max(...cumPnl);
                              const range=maxVal-minVal||1;
                              const W=320, H=48;
                              const pts=cumPnl.map((v,i)=>`${(i/(cumPnl.length-1))*W},${H-((v-minVal)/range)*H}`).join(" ");
                              const finalPnl=cumPnl[cumPnl.length-1];
                              return (
                                <div style={{marginTop:12,padding:"10px 0 0",borderTop:"1px solid #1e2130"}}>
                                  <div style={{fontSize:9,fontWeight:700,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>Equity Curve</div>
                                  <svg width={W} height={H} style={{overflow:"visible"}}>
                                    <defs>
                                      <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={finalPnl>=0?"#00c896":"#f03b3b"} stopOpacity="0.3"/>
                                        <stop offset="100%" stopColor={finalPnl>=0?"#00c896":"#f03b3b"} stopOpacity="0"/>
                                      </linearGradient>
                                    </defs>
                                    <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#eqGrad)"/>
                                    <polyline points={pts} fill="none" stroke={finalPnl>=0?"#00c896":"#f03b3b"} strokeWidth="1.5"/>
                                    <line x1="0" y1={H-((0-minVal)/range)*H} x2={W} y2={H-((0-minVal)/range)*H}
                                      stroke="#2a2d3a" strokeWidth="0.5" strokeDasharray="3,3"/>
                                  </svg>
                                  <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#4a4f5e",fontFamily:"monospace",marginTop:3}}>
                                    <span>+{closed.length} trades</span>
                                    <span style={{color:finalPnl>=0?"#00c896":"#f03b3b",fontWeight:700}}>{finalPnl>=0?"+":""}{fmt.usd(finalPnl)} net</span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </td></tr>
                      )}
                      {tab==="journal"&&(
                        <tr><td colSpan={8} style={{padding:0}}>
                          <div style={{padding:"12px 16px"}}>
                            <div style={{fontSize:9,fontWeight:700,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>
                              Trade Journal — add notes to your trades
                            </div>
                            {history.length===0&&<div style={{color:"#2a2d3a",fontSize:11,textAlign:"center",padding:"16px 0"}}>No trades to journal yet</div>}
                            <div style={{display:"flex",flexDirection:"column",gap:8}}>
                              {history.slice(0,20).map(t=>{
                                const pnl=Number(t.pnl);
                                const note=tradeNotes[t.id]||'';
                                return (
                                  <div key={t.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 10px",background:"#111318",border:"1px solid #1e2130",borderRadius:4}}>
                                    <div style={{width:3,alignSelf:"stretch",borderRadius:2,background:pnl>=0?"#00c896":"#f03b3b",flexShrink:0}}/>
                                    <div style={{width:120,flexShrink:0}}>
                                      <div style={{fontSize:11,fontWeight:700,color:"#e2e4e9",fontFamily:"monospace"}}>{t.symbol} {t.side}</div>
                                      <div style={{fontSize:10,fontFamily:"monospace",fontWeight:600,color:pnl>=0?"#00c896":"#f03b3b"}}>{pnl>=0?"+":""}{fmt.usd(pnl)}</div>
                                      <div style={{fontSize:9,color:"#4a4f5e"}}>{t.closedAt?new Date(t.closedAt).toLocaleDateString():"—"}</div>
                                    </div>
                                    <textarea
                                      value={note}
                                      onChange={e=>setTradeNotes(n=>({...n,[t.id]:e.target.value}))}
                                      placeholder="Add notes: setup, mistakes, lessons..."
                                      style={{flex:1,background:"#1a1d27",border:"1px solid #2a2d3a",borderRadius:3,
                                        color:"#a0a5b4",fontSize:11,padding:"5px 8px",resize:"vertical",minHeight:40,
                                        fontFamily:"'Inter',sans-serif",lineHeight:1.5,
                                        outline:"none",transition:"border-color 0.1s"}}
                                      onFocus={e=>e.target.style.borderColor="#3a3d4a"}
                                      onBlur={e=>e.target.style.borderColor="#2a2d3a"}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </td></tr>
                      )}
                      {tab==="orders"&&(
                        <tr><td colSpan={6} style={{textAlign:"center",color:"#2a2d3a",padding:"20px 0",fontSize:11}}>No pending orders</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── PERFORMANCE CALENDAR PANEL ── */}
            {sidePanel==='calendar'&&(
              <PerformanceCalendar history={history} onClose={()=>setSidePanel(null)}/>
            )}

            {/* ── WATCHLIST PANEL ── */}
            {sidePanel==='watchlist'&&(
              <div style={{width:240,height:"100%",background:"#0d0f14",borderLeft:"1px solid #1e2130",display:"flex",flexDirection:"column",flexShrink:0}}>
                <div style={{padding:"0 14px",height:38,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #1e2130",flexShrink:0}}>
                  <span style={{fontSize:12,fontWeight:600,color:"#a0a5b4",letterSpacing:"0.05em"}}>Watchlist</span>
                  <button onClick={()=>setSidePanel(null)} style={{background:"none",border:"none",color:"#4a4f5e",cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 2px"}}>✕</button>
                </div>
                <div style={{flex:1,overflowY:"auto"}}>
                  {CATEGORIES.map(cat=>(
                    <div key={cat}>
                      <div style={{padding:"6px 12px 3px",fontSize:9,fontWeight:700,color:"#2a2d3a",letterSpacing:"0.12em",textTransform:"uppercase",background:"#111318"}}>{cat}</div>
                      {Object.entries(INSTRUMENTS).filter(([,v])=>v.category===cat).map(([sym,info])=>{
                        const isActive=sym===symbol;
                        const baseP=BASE_PRICES[sym]||100;
                        const fakeChg=(Math.sin(sym.charCodeAt(0)*2.7+sym.charCodeAt(1||0)*1.3)*2.1);
                        const fakeDir=fakeChg>=0;
                        return (
                          <div key={sym} onClick={()=>{setSymbol(sym);chartPriceRef.current=null;}}
                            style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px",cursor:"pointer",
                              background:isActive?"rgba(0,200,150,0.06)":"transparent",borderBottom:"1px solid rgba(255,255,255,0.02)"}}
                            onMouseEnter={e=>{ if(!isActive) e.currentTarget.style.background="rgba(255,255,255,0.03)"; }}
                            onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.background="transparent"; }}>
                            <div>
                              <div style={{fontSize:11,fontWeight:700,color:isActive?"#00c896":"#e2e4e9",fontFamily:"monospace"}}>{sym}</div>
                              <div style={{fontSize:9,color:"#3a3d4a"}}>{info.exchange}</div>
                            </div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:11,fontFamily:"monospace",fontWeight:600,color:fakeDir?"#00c896":"#f03b3b"}}>{fmt.price(baseP,sym)}</div>
                              <div style={{fontSize:9,fontFamily:"monospace",color:fakeDir?"#00c896":"#f03b3b"}}>{fakeDir?"+":""}{fakeChg.toFixed(2)}%</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── DOM LADDER PANEL ── */}
            {sidePanel==='dom'&&(
              <DOMPanel symbol={symbol} bid={bid} ask={ask} livePrice={chartPriceRef} inst={inst} qty={qty} onOrder={handleOrder}/>
            )}

            {/* ── PRICE ALERTS PANEL ── */}
            {sidePanel==='alerts'&&(
              <div style={{width:300,height:"100%",background:"#0d0f14",borderLeft:"1px solid #1e2130",display:"flex",flexDirection:"column",flexShrink:0}}>
                <div style={{padding:"0 14px",height:38,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #1e2130",flexShrink:0}}>
                  <span style={{fontSize:12,fontWeight:600,color:"#a0a5b4",letterSpacing:"0.05em"}}>Price Alerts</span>
                  <button onClick={()=>setSidePanel(null)} style={{background:"none",border:"none",color:"#4a4f5e",cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 2px"}}>✕</button>
                </div>
                <div style={{padding:"12px",borderBottom:"1px solid #1e2130",flexShrink:0}}>
                  <div style={{fontSize:9,fontWeight:600,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>Add Alert — {symbol}</div>
                  <div style={{display:"flex",gap:6}}>
                    <input type="number" id="alertPriceInput" step={inst.tickSize}
                      placeholder={`Price (cur: ${fmt.price(curP,symbol)})`}
                      style={{flex:1,background:"#1a1d27",border:"1px solid #2a2d3a",color:"#e2e4e9",fontSize:11,padding:"6px 8px",borderRadius:4,fontFamily:"monospace"}}/>
                    <button onClick={()=>{
                      const v=parseFloat(document.getElementById('alertPriceInput').value);
                      if(!v||isNaN(v)) return;
                      const dir=v>curP?"above":"below";
                      setAlerts(a=>[...a,{id:++alertIdRef.current,price:v,symbol,dir,triggered:false,createdAt:Date.now()}]);
                      document.getElementById('alertPriceInput').value='';
                    }} style={{padding:"6px 12px",background:"rgba(167,139,250,0.12)",border:"1px solid rgba(167,139,250,0.4)",color:"#a78bfa",borderRadius:4,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>+ Add</button>
                  </div>
                  <div style={{fontSize:9,color:"#3a3d4a",marginTop:5}}>Alert fires when price crosses the target level</div>
                </div>
                <div style={{flex:1,overflowY:"auto",padding:"8px 12px"}}>
                  {alerts.length===0&&<div style={{textAlign:"center",color:"#2a2d3a",padding:"24px 0",fontSize:11}}>No alerts set</div>}
                  {alerts.map(a=>(
                    <div key={a.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:"1px solid #1a1d27",
                      animation:a.triggered?"alertPulse 0.5s ease":undefined}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:a.triggered?"#f5a623":"#a78bfa",flexShrink:0,
                        boxShadow:a.triggered?"0 0 8px #f5a623":"0 0 4px rgba(167,139,250,0.5)"}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,fontFamily:"monospace",fontWeight:600,color:a.triggered?"#f5a623":"#e2e4e9"}}>{a.symbol} {a.dir==="above"?"↑":"↓"} {fmt.price(a.price,a.symbol)}</div>
                        <div style={{fontSize:9,color:"#4a4f5e"}}>Price {a.dir} target{a.triggered?" · TRIGGERED":""}</div>
                      </div>
                      <button onClick={()=>setAlerts(al=>al.filter(x=>x.id!==a.id))}
                        style={{background:"none",border:"none",color:"#3a3d4a",cursor:"pointer",fontSize:14,padding:"2px 4px",lineHeight:1}}>✕</button>
                    </div>
                  ))}
                </div>
                {alerts.length>0&&(
                  <div style={{padding:"8px 12px",borderTop:"1px solid #1e2130",flexShrink:0}}>
                    <button onClick={()=>setAlerts([])} style={{width:"100%",padding:"6px",background:"none",border:"1px solid #2a2d3a",color:"#5a5f6e",borderRadius:4,fontSize:10,cursor:"pointer"}}>Clear All Alerts</button>
                  </div>
                )}
              </div>
            )}

            {/* ── DRAG HANDLE — right panel ── */}
            <div
              onMouseDown={e=>{
                e.preventDefault();
                document.body.style.cursor='ew-resize';
                document.body.style.userSelect='none';
                document.body.classList.add('drag-ew');
                draggingRef.current={type:'right',startX:e.clientX,startW:rightW};
              }}
              style={{width:5,flexShrink:0,cursor:"ew-resize",background:"transparent",borderLeft:"1px solid #1e2130",position:"relative",zIndex:10,display:"flex",alignItems:"center",justifyContent:"center"}}
              onMouseEnter={e=>e.currentTarget.style.borderLeftColor="#00c896"}
              onMouseLeave={e=>{ if(!draggingRef.current) e.currentTarget.style.borderLeftColor="#1e2130"; }}
            >
              <div style={{width:2,height:36,borderRadius:1,background:"#2a2d3a",pointerEvents:"none"}}/>
            </div>

            {/* ── RIGHT PANEL — Order Entry ── */}
            <div style={{width:rightPanelCollapsed?28:rightW,flexShrink:0,background:"#0d0f14",display:"flex",flexDirection:"column",overflow:"hidden",borderLeft:"1px solid #1e2130",transition:"width 0.15s ease",position:"relative"}}>
              {/* Collapse toggle arrow */}
              <div onClick={()=>setRightPanelCollapsed(c=>!c)} title={rightPanelCollapsed?"Expand panel":"Collapse panel"}
                style={{position:"absolute",top:"50%",left:rightPanelCollapsed?4:-8,transform:"translateY(-50%)",
                  width:16,height:32,background:"#1e2130",border:"1px solid #2a2d3a",borderRadius:3,
                  display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",zIndex:10,
                  color:"#4a4f5e",fontSize:10,transition:"left 0.15s"}}
                onMouseEnter={e=>{e.currentTarget.style.background="#2a2d3a";e.currentTarget.style.color="#a0a5b4";}}
                onMouseLeave={e=>{e.currentTarget.style.background="#1e2130";e.currentTarget.style.color="#4a4f5e";}}>
                {rightPanelCollapsed?"›":"‹"}
              </div>

              {/* Panel header */}
              <div style={{padding:"0 12px",height:36,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #1e2130",flexShrink:0}}>
                <span style={{fontSize:11,fontWeight:600,color:"#a0a5b4",letterSpacing:"0.05em"}}>Order</span>
                <div style={{display:"flex",gap:4}}>
                  <div style={{width:20,height:20,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#4a4f5e",borderRadius:3,fontSize:13}} title="Settings" onClick={()=>setShowSettings(true)}>⚙</div>
                  <div style={{width:20,height:20,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#4a4f5e",borderRadius:3,fontSize:12,fontWeight:700}} title="Hotkeys (?)" onClick={()=>setShowHotkeys(h=>!h)}>?</div>
                </div>
              </div>

              <div style={{flex:1,overflowY:"auto",padding:"10px"}}>

                {/* Contract selector */}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:9,fontWeight:600,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>Contract</div>
                  <div style={{display:"flex",gap:4,alignItems:"center"}}>
                    <select value={symbol} onChange={e=>{setSymbol(e.target.value);chartPriceRef.current=null;}}
                      style={{flex:1,background:"#1a1d27",border:"1px solid #2a2d3a",color:"#e2e4e9",fontSize:11,fontWeight:600,padding:"6px 8px",borderRadius:4,cursor:"pointer",fontFamily:"monospace"}}>
                      {CATEGORIES.map(cat=>(
                        <optgroup key={cat} label={cat}>
                          {Object.entries(INSTRUMENTS).filter(([,v])=>v.category===cat).map(([s,info])=>(
                            <option key={s} value={s}>{s} — {info.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <div style={{width:28,height:28,background:"#1a1d27",border:"1px solid #2a2d3a",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#5a5f6e",fontSize:12}}>⚙</div>
                  </div>
                </div>

                {/* Order type */}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:9,fontWeight:600,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>Order Type</div>
                  <div style={{display:"flex",gap:3,marginBottom:orderType!=="market"?6:0}}>
                    {["market","limit","stop"].map(ot=>(
                      <button key={ot} onClick={()=>setOrderType(ot)} style={{
                        flex:1,padding:"5px 4px",fontSize:10,fontWeight:600,textTransform:"uppercase",
                        letterSpacing:"0.06em",cursor:"pointer",borderRadius:3,border:"1px solid",
                        background:orderType===ot?"rgba(0,200,150,0.12)":"transparent",
                        borderColor:orderType===ot?"rgba(0,200,150,0.5)":"#2a2d3a",
                        color:orderType===ot?"#00c896":"#5a5f6e",transition:"all 0.1s",
                      }}>{ot}</button>
                    ))}
                  </div>
                  {orderType==="limit"&&(
                    <div style={{position:"relative"}}>
                      <input type="number" value={limitPrice} step={inst.tickSize}
                        onChange={e=>setLimitPrice(e.target.value)}
                        placeholder={`Limit price (${fmt.price(bid,symbol)})`}
                        style={{width:"100%",background:"#1a1d27",border:"1px solid rgba(0,200,150,0.4)",color:"#e2e4e9",fontSize:11,padding:"6px 8px 6px 28px",borderRadius:4,fontFamily:"monospace"}}/>
                      <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"#00c896",fontWeight:700}}>L</span>
                    </div>
                  )}
                  {orderType==="stop"&&(
                    <div style={{position:"relative"}}>
                      <input type="number" value={stopPrice} step={inst.tickSize}
                        onChange={e=>setStopPrice(e.target.value)}
                        placeholder={`Stop price (${fmt.price(ask,symbol)})`}
                        style={{width:"100%",background:"#1a1d27",border:"1px solid rgba(240,59,59,0.4)",color:"#e2e4e9",fontSize:11,padding:"6px 8px 6px 28px",borderRadius:4,fontFamily:"monospace"}}/>
                      <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"#f03b3b",fontWeight:700}}>S</span>
                    </div>
                  )}
                </div>

                {/* Qty */}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:9,fontWeight:600,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}># of Contracts</div>
                  <div style={{background:"#1a1d27",border:"1px solid #2a2d3a",borderRadius:4,padding:"4px 8px",display:"flex",alignItems:"center",marginBottom:6}}>
                    <button onClick={()=>setQty(q=>Math.max(1,q-1))} style={{width:22,height:22,background:"#2a2d3a",border:"none",color:"#e2e4e9",fontSize:14,cursor:"pointer",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:300,lineHeight:1}}>−</button>
                    <input type="number" value={qty} min={1} onChange={e=>setQty(Math.max(1,parseInt(e.target.value)||1))}
                      style={{flex:1,background:"none",border:"none",color:"#e2e4e9",fontSize:16,fontWeight:700,textAlign:"center",fontFamily:"monospace",width:0}}/>
                    <button onClick={()=>setQty(q=>q+1)} style={{width:22,height:22,background:"#2a2d3a",border:"none",color:"#e2e4e9",fontSize:14,cursor:"pointer",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:300,lineHeight:1}}>+</button>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:3}}>
                    {[1,3,5,10,15].map(q=>(
                      <div key={q} className={`qty-chip${qty===q?' active':''}`} onClick={()=>setQty(q)}>{q}</div>
                    ))}
                  </div>
                </div>

                {/* Bracket Order — SL / TP */}
                {(()=>{
                  const [slEnabled,setSlEnabled] = [false,()=>{}]; // placeholder - use outer state
                  return null;
                })()}
                <BracketOrderPanel inst={inst} bid={bid} ask={ask} qty={qty} symbol={symbol}
                  onBracket={(sl,tp)=>{ /* stored locally, sent with order */ }}/>

                {/* Bid / Ask */}
                <div style={{background:"#111318",border:"1px solid #1e2130",borderRadius:4,padding:"8px 10px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:9,color:"#f03b3b",fontWeight:600,letterSpacing:"0.08em",marginBottom:2}}>Bid</div>
                    <div style={{fontSize:16,fontWeight:700,color:"#f03b3b",fontFamily:"monospace"}}>{fmt.price(bid,symbol)}</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#e2e4e9",fontFamily:"monospace"}}>{fmt.price((bid+ask)/2,symbol)}</div>
                    <div style={{fontSize:9,color:"#4a4f5e",fontFamily:"monospace"}}>{((ask-bid)/inst.tickSize).toFixed(0)}t spread</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:9,color:"#00c896",fontWeight:600,letterSpacing:"0.08em",marginBottom:2}}>Ask</div>
                    <div style={{fontSize:16,fontWeight:700,color:"#00c896",fontFamily:"monospace"}}>{fmt.price(ask,symbol)}</div>
                  </div>
                </div>

                {/* Account info — real data from API */}
                <div style={{background:"#111318",border:"1px solid #1e2130",borderRadius:4,padding:"8px 10px",marginBottom:10}}>
                  <div style={{fontSize:9,color:"#4a4f5e",fontWeight:500,letterSpacing:"0.06em",marginBottom:6,textTransform:"uppercase",display:"flex",justifyContent:"space-between"}}>
                    <span>{account?.name||"Practice Account"}</span>
                    <span style={{color:mdConnected?"#00c896":"#3a3d4a",fontSize:8}}>{mdConnected?"● LIVE":"● SIM"}</span>
                  </div>
                  {[
                    {l:"Balance", v:bal>0?fmt.usd(bal):"—",              c:"#e2e4e9"},
                    {l:"MLL",     v:mll!=null?fmt.usd(mll):"—",          c:"#f03b3b"},
                    {l:"Day P&L", v:(dPnl>=0?"+":"")+fmt.usd(dPnl),      c:dPnl>=0?"#00c896":"#f03b3b"},
                  ].map(r=>(
                    <div key={r.l} style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:10,color:"#4a4f5e"}}>{r.l}</span>
                      <span style={{fontSize:11,fontFamily:"monospace",fontWeight:600,color:r.c}}>{r.v}</span>
                    </div>
                  ))}
                  <div style={{height:1,background:"#1e2130",margin:"6px 0"}}/>
                  {positions.filter(p=>p.symbol===symbol).length===0
                    ? <div style={{fontSize:11,color:"#3a3d4a",textAlign:"center",padding:"4px 0"}}>No Active Position</div>
                    : positions.filter(p=>p.symbol===symbol).map(p=>{
                        const c=chartPriceRef.current||parseFloat(p.entryPrice);
                        const u=(p.side==='LONG'?c-parseFloat(p.entryPrice):parseFloat(p.entryPrice)-c)*Number(p.quantity);
                        return <div key={p.id}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                            <span style={{fontSize:11,color:p.side==="LONG"?"#00c896":"#f03b3b",fontWeight:700}}>{p.side} × {p.quantity}</span>
                            <span style={{fontSize:11,fontFamily:"monospace",fontWeight:700,color:u>=0?"#00c896":"#f03b3b"}}>{u>=0?"+":""}{fmt.usd(u)}</span>
                          </div>
                          <div style={{fontSize:9,color:"#4a4f5e",fontFamily:"monospace"}}>Entry: {parseFloat(p.entryPrice).toFixed(2)}</div>
                        </div>;
                      })
                  }
                </div>


                {/* BUY / SELL */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,padding:"8px 10px",borderTop:"1px solid #1e2130"}}>
                  <button onClick={()=>handleOrder('buy')} className="order-btn"
                    style={{background:"#00c896",color:"#000",fontSize:11}}
                    onMouseEnter={e=>e.currentTarget.style.background="#00e5a8"}
                    onMouseLeave={e=>e.currentTarget.style.background="#00c896"}>
                    BUY +{qty} @ MARKET
                  </button>
                  <button onClick={()=>handleOrder('sell')} className="order-btn"
                    style={{background:"#f03b3b",color:"#fff",fontSize:11}}
                    onMouseEnter={e=>e.currentTarget.style.background="#ff5555"}
                    onMouseLeave={e=>e.currentTarget.style.background="#f03b3b"}>
                    SELL -{qty} @ MARKET
                  </button>
                </div>

                {/* JOIN BID / ASK */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6,padding:"0 10px 8px"}}>
                  {[{l:"JOIN BID",side:"sell"},{l:"JOIN ASK",side:"buy"}].map(({l,side})=>(
                    <button key={l} onClick={()=>handleOrder(side)} className="order-btn"
                      style={{background:"#1a1d27",border:"1px solid #2a2d3a",color:"#a0a5b4",fontSize:10,padding:"7px"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor="#3a3d4a"}
                      onMouseLeave={e=>e.currentTarget.style.borderColor="#2a2d3a"}>{l}</button>
                  ))}
                </div>

                {/* CLOSE / REVERSE */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
                  {[
                    {l:"CLOSE POSITION",  fn:()=>flattenAll(),                                                       disabled:positions.length===0},
                    {l:"REVERSE POSITION",fn:()=>{const p=positions.find(x=>x.symbol===symbol);if(p){const s=p.side==='LONG'?'sell':'buy';closePos(p).then(()=>handleOrder(s));}}, disabled:positions.length===0},
                  ].map(({l,fn,disabled})=>(
                    <button key={l} onClick={fn} disabled={disabled} className="order-btn"
                      style={{background:disabled?"#111318":"#1a1d27",border:"1px solid",borderColor:disabled?"#1e2130":"#2a2d3a",color:disabled?"#2a2d3a":"#a0a5b4",fontSize:10,padding:"7px",opacity:disabled?0.5:1}}
                      onMouseEnter={e=>{if(!disabled)e.currentTarget.style.borderColor="#3a3d4a";}}
                      onMouseLeave={e=>{if(!disabled)e.currentTarget.style.borderColor="#2a2d3a";}}>{l}</button>
                  ))}
                </div>
                {/* ── Position Management ── */}
                {positions.length>0&&(()=>{
                  const curPos=positions.find(p=>p.symbol===symbol);
                  const beOn=curPos&&beStopIds.has(curPos.id);
                  const trailOn=curPos&&trailStop?.posId===curPos?.id;
                  if(!curPos) return null;
                  return (
                    <div style={{background:"#111318",border:"1px solid #1e2130",borderRadius:4,padding:"8px 10px",marginBottom:6}}>
                      <div style={{fontSize:9,fontWeight:700,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>Position Management</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,marginBottom:trailOn?6:0}}>
                        <button onClick={()=>{
                          if(beOn){setBeStopIds(s=>{const n=new Set(s);n.delete(curPos.id);return n;});toast('BE stop removed','info');}
                          else{setBeStopIds(s=>new Set([...s,curPos.id]));toast(`BE stop set @ ${fmt.price(parseFloat(curPos.entryPrice),symbol)}`,'info');}
                        }} style={{padding:"5px",background:beOn?"rgba(0,200,150,0.1)":"#1a1d27",border:"1px solid",borderColor:beOn?"rgba(0,200,150,0.4)":"#2a2d3a",color:beOn?"#00c896":"#7a7f8e",borderRadius:3,fontSize:9,fontWeight:700,cursor:"pointer",textTransform:"uppercase",letterSpacing:"0.04em"}}>
                          {beOn?"✓ ":""}BE Stop
                        </button>
                        <button onClick={()=>{
                          if(trailOn){setTrailStop(null);toast('Trail stop removed','info');}
                          else{const entry=parseFloat(curPos.entryPrice);setTrailStop({posId:curPos.id,ticks:10,highWater:entry});toast('Trailing stop: 10 ticks','info');}
                        }} style={{padding:"5px",background:trailOn?"rgba(245,166,35,0.1)":"#1a1d27",border:"1px solid",borderColor:trailOn?"rgba(245,166,35,0.4)":"#2a2d3a",color:trailOn?"#f5a623":"#7a7f8e",borderRadius:3,fontSize:9,fontWeight:700,cursor:"pointer",textTransform:"uppercase",letterSpacing:"0.04em"}}>
                          {trailOn?"✓ ":""}Trail Stop
                        </button>
                      </div>
                      {trailOn&&(
                        <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                          <span style={{fontSize:9,color:"#4a4f5e",flexShrink:0}}>Ticks:</span>
                          <input type="number" value={trailStop.ticks} min={1} max={100}
                            onChange={e=>setTrailStop(t=>({...t,ticks:Math.max(1,parseInt(e.target.value)||1)}))}
                            style={{width:44,background:"#1a1d27",border:"1px solid #2a2d3a",color:"#f5a623",borderRadius:3,fontSize:11,padding:"2px 5px",fontFamily:"monospace",textAlign:"center"}}/>
                          <span style={{fontSize:9,color:"#4a4f5e"}}>= ${((trailStop.ticks||1)*inst.tickVal).toFixed(0)}/ct</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* CANCEL / FLATTEN */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
                  <button className="order-btn" style={{background:"#1a1d27",border:"1px solid #2a2d3a",color:"#7a7f8e",fontSize:10,padding:"7px",opacity:0.5}} disabled>CANCEL ORDERS</button>
                  <button onClick={flattenAll} disabled={positions.length===0} className="order-btn"
                    style={{background:positions.length?"rgba(240,59,59,0.1)":"#111318",border:"1px solid",borderColor:positions.length?"rgba(240,59,59,0.35)":"#1e2130",color:positions.length?"#f03b3b":"#2a2d3a",fontSize:10,padding:"7px"}}
                    onMouseEnter={e=>{if(positions.length)e.currentTarget.style.background="rgba(240,59,59,0.18)";}}
                    onMouseLeave={e=>{if(positions.length)e.currentTarget.style.background="rgba(240,59,59,0.1)";}}>FLATTEN ALL</button>
                </div>

                {/* Risk Calculator */}
                <div style={{background:"#111318",border:"1px solid #1e2130",borderRadius:4,padding:"8px 10px"}}>
                  <div style={{fontSize:9,fontWeight:600,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>Risk Calculator</div>
                  {[
                    ["Tick Value", `$${inst.tickVal}/tick`, "#a0a5b4"],
                    ["1-Tick Risk", `$${(inst.tickVal*qty).toFixed(2)}`, "#a0a5b4"],
                    ["5-Tick Risk", `$${(inst.tickVal*5*qty).toFixed(2)}`, "#f5a623"],
                    ["10-Tick Risk",`$${(inst.tickVal*10*qty).toFixed(2)}`, "#f03b3b"],
                    ["Point Value", `$${(inst.tickVal/inst.tickSize*qty).toFixed(2)}/pt`, "#7a7f8e"],
                  ].map(([l,v,c])=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:10,color:"#4a4f5e"}}>{l}</span>
                      <span style={{fontSize:10,fontFamily:"monospace",fontWeight:600,color:c}}>{v}</span>
                    </div>
                  ))}
                  {bal>0&&(
                    <div style={{borderTop:"1px solid #1e2130",marginTop:5,paddingTop:5}}>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span style={{fontSize:10,color:"#4a4f5e"}}>5-Tick / Balance</span>
                        <span style={{fontSize:10,fontFamily:"monospace",fontWeight:600,color:(inst.tickVal*5*qty/bal*100)>2?"#f03b3b":"#5a5f6e"}}>{(inst.tickVal*5*qty/bal*100).toFixed(2)}%</span>
                      </div>
                    </div>
                  )}
                </div>

              </div>{/* end order scroll */}

            </div>
          </div>
        </div>
      </div>

      {/* Trade Replay */}
      {showReplay&&<TradeReplay history={history} symbol={symbol} onClose={()=>setShowReplay(false)}/>}

      {/* Analytics Dashboard */}
      {showAnalytics&&<AnalyticsDashboard history={history} balance={bal||50000} onClose={()=>setShowAnalytics(false)}/>}

      {/* Hotkey Overlay */}
      {showHotkeys&&(
        <div style={{position:"fixed",inset:0,zIndex:99999,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setShowHotkeys(false)}>
          <div style={{background:"#141720",border:"1px solid #2a2d3a",borderRadius:10,padding:"28px 32px",width:580,maxWidth:"90vw",boxShadow:"0 32px 80px rgba(0,0,0,0.9)",animation:"fadeUp 0.2s ease"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <span style={{fontSize:15,fontWeight:700,color:"#e2e4e9",letterSpacing:"0.03em"}}>Keyboard Shortcuts</span>
              <button onClick={()=>setShowHotkeys(false)} style={{background:"none",border:"none",color:"#4a4f5e",cursor:"pointer",fontSize:18,lineHeight:1}}>✕</button>
            </div>
            {[
              {cat:"Trading",keys:[
                {k:"B",d:"Buy Market"},
                {k:"S",d:"Sell Market"},
                {k:"F",d:"Flatten All Positions"},
                {k:"+",d:"Increase Qty"},
                {k:"−",d:"Decrease Qty"},
              ]},
              {cat:"Chart",keys:[
                {k:"H",d:"Horizontal Line Tool"},
                {k:"T",d:"Trendline Tool"},
                {k:"D",d:"Clear All Drawings"},
                {k:"Scroll",d:"Pan Left / Right"},
                {k:"Ctrl+Scroll",d:"Zoom In / Out"},
                {k:"Drag",d:"Pan Chart"},
                {k:"Right-click",d:"Context Menu / Set Alert"},
              ]},
              {cat:"Interface",keys:[
                {k:"? or /",d:"Show / Hide This Help"},
                {k:"Esc",d:"Close Modals / Cancel Tool"},
                {k:"Indicators btn",d:"Toggle EMA/SMA overlays"},
                {k:"Bell icon",d:"Price Alerts panel"},
                {k:"List icon",d:"Watchlist"},
              ]},
            ].map(section=>(
              <div key={section.cat} style={{marginBottom:16}}>
                <div style={{fontSize:9,fontWeight:700,color:"#4a4f5e",letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:8}}>{section.cat}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 24px"}}>
                  {section.keys.map(({k,d})=>(
                    <div key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0"}}>
                      <kbd style={{
                        background:"#1e2130",border:"1px solid #3a3d4a",borderBottom:"2px solid #2a2d3a",
                        borderRadius:4,padding:"2px 8px",fontSize:11,fontFamily:"monospace",
                        color:"#00c896",fontWeight:700,whiteSpace:"nowrap",minWidth:32,textAlign:"center",
                        boxShadow:"inset 0 1px 0 rgba(255,255,255,0.05)"
                      }}>{k}</kbd>
                      <span style={{fontSize:11,color:"#7a7f8e"}}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #1e2130",fontSize:10,color:"#3a3d4a",textAlign:"center"}}>
              Press <kbd style={{background:"#1e2130",border:"1px solid #2a2d3a",borderRadius:3,padding:"1px 6px",fontSize:10,color:"#5a5f6e",fontFamily:"monospace"}}>?</kbd> or click anywhere outside to close
            </div>
          </div>
        </div>
      )}

      {/* Context menu */}
      {ctxMenu&&<ContextMenu x={ctxMenu.x} y={ctxMenu.y} onClose={()=>setCtxMenu(null)}
        onSettings={()=>setShowSettings(true)}
        onHLine={()=>setActiveTool('hline')}
        onTrendline={()=>setActiveTool('trendline')}
        onClearDrawings={()=>drawingsRef.current?.clear()}
        onResetZoom={()=>drawingsRef.current?.resetZoom()}
        onSetAlert={()=>{
          const p=chartPriceRef.current||BASE_PRICES[symbol];
          if(!p) return;
          setAlerts(a=>[...a,{id:++alertIdRef.current,price:p,symbol,dir:"both",triggered:false,createdAt:Date.now()}]);
          setSidePanel("alerts");
          toast(`🔔 Alert set at ${fmt.price(p,symbol)}`,"info");
        }}
      />}

      {/* Settings modal */}
      {showSettings&&<SettingsModal settings={chartSettings} onChange={s=>setChartSettings(s)} onClose={()=>setShowSettings(false)} symbol={symbol} timeframe={timeframe}/>}

      {/* Toasts */}
      <div style={{position:"fixed",top:46,right:16,zIndex:9999,display:"flex",flexDirection:"column",gap:6,pointerEvents:"none"}}>
        {toasts.map(t=>(
          <div key={t.id} style={{background:"#1a1d27",border:`1px solid ${t.type==='buy'?'#00c896':t.type==='sell'?'#f03b3b':t.type==='success'?'#00c896':t.type==='error'?'#f03b3b':'#2a2d3a'}`,
            padding:"8px 16px",fontSize:11,borderRadius:4,animation:"slideIn 0.2s ease",whiteSpace:"nowrap",
            color:t.type==='buy'?'#00c896':t.type==='sell'?'#f03b3b':t.type==='success'?'#00c896':t.type==='error'?'#f03b3b':'#a0a5b4',
            boxShadow:"0 4px 20px rgba(0,0,0,0.5)"}}>
            {t.msg}
          </div>
        ))}
      </div>
    </>
  );
}
